import { useState } from 'react'
import {
  MicroscopeIcon,
  SinglePlan,
  Task,
  UpdateTask,
  useAddContractAnalysisTypeMutation,
  useGetContractsQuery,
  ErrorMessage,
  ModalDialog,
  Mtds,
  templates,
  useGetBatchDataQuery,
  useGetPurposesQuery,
  useGetSubPurposesQuery,
  useUpdateTaskMutation,
  toUserMessage,
  widgets
} from 'prove-common'
import { useQueryClient } from '@tanstack/react-query'
import { useExistingOrCreateAnalysisType } from '../../hooks/useExistingOrCreateAnalysisType'
import { getTaskSchema, type TaskFormData } from '../../schemas/task-schema'
import {
  filterBatchData,
  findValidContractAnalysisTypes
} from '../../utils/taskUtils'
import { getEditTaskUiSchema } from '../../schemas/task-ui-schema'
import { Form, IChangeEvent, RJSFSchema, validator } from 'prove-common/rjsf'
import { useTaskReceptionAddress } from '../../hooks/useTaskReceptionAddress'
import { TaskReceptionAddress } from '../task-reception-address/TaskReceptionAddress'

type EditTaskCardProps = {
  isOpen: boolean
  setOpen: (isOpen: boolean) => void
  task: Task
  plan: SinglePlan
}

export default function EditTaskCard(props: Readonly<EditTaskCardProps>) {
  return props.isOpen ? <EditTaskForm key={props.task.id} {...props} /> : null
}

function EditTaskForm({
  isOpen,
  setOpen,
  task: latestTask,
  plan
}: Readonly<EditTaskCardProps>) {
  // Keep the draft and its original ETag together until the editor is reopened.
  const [task] = useState(latestTask)
  // Prefer task.contractId; if missing, fall back to plan's contract
  const selectedContractId = task.contractId ?? plan.contractId

  const [formData, setFormData] = useState<Partial<TaskFormData>>({
    labId: task.lab?.id,
    labReceptionAddressId: task.labReceptionAddressId,
    contractId: selectedContractId,
    quantity: task.quantity,
    countSubSamples: task.countSubSamples,
    sampntTypeId: task.sampntType?.id,
    productId: task.analysisType?.sampleType?.productId,
    matrixId: task.analysisType?.sampleType?.matrixId,
    substanceIds: task.analysisType?.substances?.map(
      (substance) => substance.substance.id
    ),
    payloadSchemaId: task.analysisType?.payloadSchema?.id,
    ordningsverdiId: task.ordningsVerdi?.id,
    subPurposeId: task.subPurposeId,
    locationRequired: task.locationRequired
  })
  const [saveErrorMessage, setSaveErrorMessage] = useState<string>()
  const receptionAddress = useTaskReceptionAddress(
    formData.labId,
    formData.labReceptionAddressId,
    isOpen
  )
  const selectedAddressId = formData.labReceptionAddressId
  const { data: contracts } = useGetContractsQuery(isOpen)
  const parentContract = selectedContractId
    ? contracts?.find((contract) => contract.id === selectedContractId)
    : undefined
  const {
    data: batchData,
    isPending: isPendingBatchData,
    isError: isErrorBatchData,
    error: batchDataError
  } = useGetBatchDataQuery(isOpen)
  const { data: subPurposes } = useGetSubPurposesQuery(isOpen)
  const { data: purposes } = useGetPurposesQuery(isOpen)
  const purposeName = purposes?.find((r) => r.id === plan.purposeId)?.name
  const editTaskUiSchema = getEditTaskUiSchema(purposeName)
  const sampleTypes = batchData?.sampleTypes
  const analysisTypes = batchData?.analysisTypes
  const updateTask = useUpdateTaskMutation()
  const existingOrCreateAnalysisType = useExistingOrCreateAnalysisType(
    sampleTypes,
    analysisTypes
  )
  const addContractAnalysisType = useAddContractAnalysisTypeMutation()
  const validAnalysisTypes = findValidContractAnalysisTypes({
    formData,
    contracts
  })
  const { valid, invalid } = batchData
    ? filterBatchData(batchData, validAnalysisTypes)
    : { valid: null, invalid: null }
  const fullSchema: RJSFSchema | null =
    valid && invalid
      ? getTaskSchema(valid, invalid, contracts ?? [], subPurposes ?? [])
      : null
  const queryClient = useQueryClient()
  const isSaving =
    existingOrCreateAnalysisType.isPending ||
    addContractAnalysisType.isPending ||
    updateTask.isPending

  const onSubmit = async ({
    formData
  }: IChangeEvent<TaskFormData>): Promise<void> => {
    if (!receptionAddress.canSave) return
    if (formData != null && task.etag != null && task.analysisType != null) {
      try {
        setSaveErrorMessage(undefined)
        const analysisType = await existingOrCreateAnalysisType.mutateAsync({
          selectedProductId: formData?.productId,
          selectedMatrixId: formData?.matrixId,
          selectedSubstanceIds: formData.substanceIds,
          selectedPayloadSchemaId: formData.payloadSchemaId
        })

        // Add analysis type to contract if not already added
        const contract = contracts?.find((c) => c.id === formData.contractId)
        if (
          contract &&
          !contract.analysisTypes?.some(
            (a) => a.analysisType?.id === analysisType.id
          )
        ) {
          await addContractAnalysisType
            .mutateAsync({
              contractId: contract.id,
              body: { analysisTypeId: analysisType.id }
            })
            .then(() =>
              Mtds.toast.success('Analysetypen ble lagt til i kontrakten.')
            )
        }

        const updatedTask: UpdateTask = {
          quantity: formData.quantity,
          countSubSamples: formData.countSubSamples,
          sampntTypeId: formData.sampntTypeId,
          labId: formData.labId,
          labReceptionAddressId: selectedAddressId,
          description: task.description,
          ownerId: task.ownerId,
          analysisTypeId: analysisType.id,
          planStatus: task.planStatus,
          ordningsverdiId: formData.ordningsverdiId,
          locationRequired: formData.locationRequired,
          contractId: formData.contractId,
          subPurposeId: formData.subPurposeId,
          duedates: task.duedates,
          matrixAmount: task.matrixAmount ?? null
        }
        await updateTask
          .mutateAsync({
            taskId: task.id,
            etag: task.etag,
            body: updatedTask
          })
          .then(async () => {
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ['tasks', task.id] }),
              queryClient.invalidateQueries({ queryKey: ['plans', plan.id] })
            ])
            setOpen(false)
          })
      } catch (e) {
        console.error(e)
        setSaveErrorMessage(toUserMessage(e))
      }
    }
  }

  return (
    <ModalDialog
      id={'edit-task-drawer'}
      closedby='any'
      data-placement='right'
      open={isOpen}
      onClose={() => setOpen(false)}
    >
      <Mtds.Button
        command='close'
        commandfor={'edit-task-drawer'}
        aria-label={'Lukk'}
      />
      <Mtds.Prose>
        <Mtds.Heading>Rediger oppgave</Mtds.Heading>
        <Mtds.Info>
          <MicroscopeIcon />
          {parentContract?.title}
        </Mtds.Info>
        {isPendingBatchData && <Mtds.Skeleton />}
        {isErrorBatchData && <ErrorMessage error={batchDataError} />}
        {formData.labId != null && (
          <TaskReceptionAddress
            query={receptionAddress}
            value={formData.labReceptionAddressId}
            disabled={isSaving}
            onChange={(labReceptionAddressId) =>
              setFormData((current) => ({ ...current, labReceptionAddressId }))
            }
          />
        )}
        {!isPendingBatchData && !isErrorBatchData && (
          <Form
            noHtml5Validate
            schema={fullSchema ?? {}}
            uiSchema={editTaskUiSchema}
            widgets={widgets}
            templates={templates}
            showErrorList={'bottom'}
            validator={validator}
            formData={formData}
            formContext={{
              contractId: formData.contractId
            }}
            onSubmit={(data) => onSubmit(data)}
            onChange={(data) => {
              setSaveErrorMessage(undefined)
              setFormData((current) => ({ ...current, ...data.formData }))
            }}
          >
            {saveErrorMessage && (
              <Mtds.Validation className='mt-4 whitespace-normal break-words'>
                Kunne ikke lagre oppgaven: {saveErrorMessage}
              </Mtds.Validation>
            )}
            <Mtds.Flex className='mt-8'>
              <Mtds.Button command='close' commandfor={'edit-task-drawer'}>
                Forkast
              </Mtds.Button>
              <Mtds.Button
                type='submit'
                data-variant='primary'
                aria-busy={isSaving}
                disabled={isSaving || !receptionAddress.canSave}
              >
                Lagre
              </Mtds.Button>
            </Mtds.Flex>
          </Form>
        )}
      </Mtds.Prose>
    </ModalDialog>
  )
}
